<template>
  <div class="container">
    <BasicTitle :title="$route.name" />
    <BasicSubtitle subtitle="Configura como se calcularan los cierres mensuales del edificio seleccionado." />

    <div v-if="!buildingId" class="notice">
      Debe seleccionar un edificio para configurar cierres mensuales.
    </div>

    <form v-else class="configuration-form" @submit.prevent="onSave">
      <section class="form-section">
        <h2>Gasto comun</h2>
        <BasicLabel label="Tipo de gasto comun *" />
        <BorderSelect
          v-model="form.closingType"
          label="Tipo de gasto comun *"
          :options="closingTypeOptions"
        />
        <FormError :show="Boolean(errors.closingType)" :message="errors.closingType" />

        <template v-if="form.closingType === 'fixed'">
          <BasicLabel label="Monto total mensual de gasto comun *" />
          <BorderInput
            v-model="form.commonExpenseFixedAmount"
            label="Monto total mensual"
            type="number"
            step="0.01"
            min="0"
          />
          <FormError
            :show="Boolean(errors.commonExpenseFixedAmount)"
            :message="errors.commonExpenseFixedAmount"
          />
        </template>

        <template v-if="form.closingType === 'fixed_per_unit'">
          <BasicLabel label="Monto fijo por apartamento *" />
          <BorderInput
            v-model="form.commonExpenseUnitAmount"
            label="Monto fijo por apartamento"
            type="number"
            step="0.01"
            min="0"
          />
          <FormError
            :show="Boolean(errors.commonExpenseUnitAmount)"
            :message="errors.commonExpenseUnitAmount"
          />
        </template>

        <p v-if="form.closingType === 'variable'" class="helper">
          Se calculara usando los egresos del mes.
        </p>
      </section>

      <section class="form-section">
        <h2>Fondo de reserva</h2>
        <BasicLabel label="Tipo de fondo de reserva *" />
        <BorderSelect
          v-model="form.reserveFundType"
          label="Tipo de fondo de reserva *"
          :options="reserveFundTypeOptions"
        />
        <FormError :show="Boolean(errors.reserveFundType)" :message="errors.reserveFundType" />

        <template v-if="form.reserveFundType === 'fixed'">
          <BasicLabel label="Monto mensual total del fondo de reserva *" />
          <BorderInput
            v-model="form.reserveFundValue"
            label="Monto mensual total"
            type="number"
            step="0.01"
            min="0"
          />
          <FormError
            :show="Boolean(errors.reserveFundValue)"
            :message="errors.reserveFundValue"
          />
        </template>

        <template v-if="form.reserveFundType === 'variable'">
          <BasicLabel label="Monto anual objetivo *" />
          <BorderInput
            v-model="form.reserveFundValue"
            label="Monto anual objetivo"
            type="number"
            step="0.01"
            min="0"
          />
          <FormError
            :show="Boolean(errors.reserveFundValue)"
            :message="errors.reserveFundValue"
          />

          <BasicLabel label="Porcentaje de recaudacion *" />
          <BorderInput
            v-model="form.reserveFundPercentage"
            label="Porcentaje de recaudacion"
            type="number"
            step="0.0001"
            min="0"
          />
          <FormError
            :show="Boolean(errors.reserveFundPercentage)"
            :message="errors.reserveFundPercentage"
          />
        </template>

        <template v-if="form.reserveFundType === 'fixed_per_unit'">
          <BasicLabel label="Monto fijo de fondo por apartamento *" />
          <BorderInput
            v-model="form.reserveFundUnitAmount"
            label="Monto fijo de fondo"
            type="number"
            step="0.01"
            min="0"
          />
          <FormError
            :show="Boolean(errors.reserveFundUnitAmount)"
            :message="errors.reserveFundUnitAmount"
          />
        </template>

        <p v-if="form.reserveFundType === 'none'" class="helper">
          No se calculara fondo de reserva.
        </p>
      </section>

      <section class="form-section">
        <h2>Prorrateo</h2>
        <BasicLabel label="Metodo de prorrateo *" />
        <BorderSelect
          v-model="form.prorationMethod"
          label="Metodo de prorrateo *"
          :options="prorationMethodOptions"
        />
        <FormError :show="Boolean(errors.prorationMethod)" :message="errors.prorationMethod" />
        <p class="helper">
          El sistema utilizara el coeficiente cargado en cada apartamento.
        </p>
      </section>

      <section class="form-section">
        <h2>Fechas</h2>
        <BasicLabel label="Dia sugerido de cierre" />
        <BorderInput
          v-model="form.closingDay"
          label="Dia sugerido de cierre"
          type="number"
          min="1"
          max="31"
        />
        <FormError :show="Boolean(errors.closingDay)" :message="errors.closingDay" />

        <BasicLabel label="Dia de vencimiento" />
        <BorderInput
          v-model="form.dueDay"
          label="Dia de vencimiento"
          type="number"
          min="1"
          max="31"
        />
        <FormError :show="Boolean(errors.dueDay)" :message="errors.dueDay" />

        <BasicLabel label="Dias de gracia" />
        <BorderInput
          v-model="form.graceDays"
          label="Dias de gracia"
          type="number"
          min="0"
        />
        <FormError :show="Boolean(errors.graceDays)" :message="errors.graceDays" />
      </section>

      <section class="form-section">
        <h2>Moneda</h2>
        <BasicLabel label="Moneda principal" />
        <BorderSelect
          v-model="form.mainCurrencyId"
          label="Moneda principal"
          :options="currencyOptions"
        />
        <FormError :show="Boolean(errors.mainCurrencyId)" :message="errors.mainCurrencyId" />
      </section>

      <section class="form-section">
        <h2>Reglas informativas</h2>
        <ul class="rules">
          <li>El gasto comun se carga al residente/inquilino si existe; si no, al propietario.</li>
          <li>El fondo de reserva siempre se carga al propietario.</li>
          <li>Las multas se cargan al residente si existe; si no, al propietario.</li>
        </ul>
      </section>

      <FormError :show="Boolean(generalError)" :message="generalError" />

      <div class="actions">
        <PrimaryButton label="Guardar" :click="onSave" />
        <button type="button" class="secondary-button" @click="onCancel">Cancelar</button>
      </div>
    </form>
  </div>
</template>

<script>
import { computed, onMounted, reactive, watch } from "vue";
import { useRouter } from "vue-router";
import store from "../../store";
import BasicTitle from "../../widgets/title/BasicTitle";
import BasicSubtitle from "../../widgets/subtitle/BasicSubtitle";
import BasicLabel from "../../widgets/label/BasicLabel";
import BorderInput from "../../widgets/input/BorderInput";
import BorderSelect from "../../widgets/select/BorderSelect";
import FormError from "../../widgets/error/FormError";
import PrimaryButton from "../../widgets/button/PrimaryButton";
import { AccountingConfigurationService } from "../../services";

const defaultForm = () => ({
  closingType: "variable",
  commonExpenseFixedAmount: "",
  commonExpenseUnitAmount: "",
  reserveFundType: "none",
  reserveFundValue: "",
  reserveFundPercentage: "",
  reserveFundUnitAmount: "",
  prorationMethod: "apportionment",
  mainCurrencyId: "",
  closingDay: "",
  dueDay: "",
  graceDays: "0",
});

const createErrors = () => ({
  closingType: "",
  commonExpenseFixedAmount: "",
  commonExpenseUnitAmount: "",
  reserveFundType: "",
  reserveFundValue: "",
  reserveFundPercentage: "",
  reserveFundUnitAmount: "",
  prorationMethod: "",
  mainCurrencyId: "",
  closingDay: "",
  dueDay: "",
  graceDays: "",
});

const toOptionList = (items = []) =>
  (items || [])
    .map((item) => {
      const id = item.id ?? item.value;
      const label = [item.code, item.name || item.label].filter(Boolean).join(" - ");
      return id !== undefined && label ? { id: String(id), name: label } : null;
    })
    .filter(Boolean);

export default {
  components: {
    BasicTitle,
    BasicSubtitle,
    BasicLabel,
    BorderInput,
    BorderSelect,
    FormError,
    PrimaryButton,
  },
  setup() {
    const router = useRouter();
    const form = reactive(defaultForm());
    const errors = reactive(createErrors());
    const state = reactive({
      generalError: "",
    });

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (value) return typeof value === "object" ? value.id : value;
      const buildings = store.state.auth.user?.buildings || [];
      return buildings.length === 1 ? buildings[0].id : null;
    });

    const closingTypeOptions = [
      { id: "variable", name: "Variable" },
      { id: "fixed", name: "Fijo" },
      { id: "fixed_per_unit", name: "Fijo por unidad" },
    ];
    const reserveFundTypeOptions = [
      { id: "none", name: "No aplica" },
      { id: "fixed", name: "Fijo" },
      { id: "variable", name: "Variable" },
      { id: "fixed_per_unit", name: "Fijo por unidad" },
    ];
    const prorationMethodOptions = [
      { id: "apportionment", name: "Por coeficiente del apartamento" },
    ];
    const currencyOptions = computed(() => [
      { id: "", name: "Sin moneda principal" },
      ...toOptionList(store.state.general.currencies),
    ]);
    const generalError = computed(() => state.generalError);

    const resetErrors = () => {
      Object.keys(errors).forEach((key) => {
        errors[key] = "";
      });
      state.generalError = "";
    };

    const assignForm = (configuration = {}) => {
      const values = {
        ...defaultForm(),
        ...configuration,
      };
      Object.keys(defaultForm()).forEach((key) => {
        form[key] =
          values[key] === undefined || values[key] === null ? "" : String(values[key]);
      });
      form.closingType = values.closingType || "variable";
      form.reserveFundType = values.reserveFundType || "none";
      form.prorationMethod = values.prorationMethod || "apportionment";
      form.graceDays =
        values.graceDays === undefined || values.graceDays === null
          ? "0"
          : String(values.graceDays);
    };

    const isNumber = (value) => value !== "" && Number.isFinite(Number(value));
    const isNonNegative = (value) => isNumber(value) && Number(value) >= 0;
    const validateDay = (key, label) => {
      if (form[key] === "") return;
      const value = Number(form[key]);
      if (!Number.isInteger(value) || value < 1 || value > 31) {
        errors[key] = `${label} debe estar entre 1 y 31.`;
      }
    };

    const validate = () => {
      resetErrors();
      if (!form.closingType) errors.closingType = "Seleccione el tipo de gasto comun.";
      if (form.closingType === "fixed" && !isNonNegative(form.commonExpenseFixedAmount)) {
        errors.commonExpenseFixedAmount = "Ingrese un monto mayor o igual a 0.";
      }
      if (form.closingType === "fixed_per_unit" && !isNonNegative(form.commonExpenseUnitAmount)) {
        errors.commonExpenseUnitAmount = "Ingrese un monto mayor o igual a 0.";
      }

      if (!form.reserveFundType) errors.reserveFundType = "Seleccione el tipo de fondo.";
      if (form.reserveFundType === "fixed" && !isNonNegative(form.reserveFundValue)) {
        errors.reserveFundValue = "Ingrese un monto mayor o igual a 0.";
      }
      if (form.reserveFundType === "variable") {
        if (!isNonNegative(form.reserveFundValue)) {
          errors.reserveFundValue = "Ingrese un monto anual mayor o igual a 0.";
        }
        if (!isNonNegative(form.reserveFundPercentage)) {
          errors.reserveFundPercentage = "Ingrese un porcentaje mayor o igual a 0.";
        }
      }
      if (form.reserveFundType === "fixed_per_unit" && !isNonNegative(form.reserveFundUnitAmount)) {
        errors.reserveFundUnitAmount = "Ingrese un monto mayor o igual a 0.";
      }

      if (form.prorationMethod !== "apportionment") {
        errors.prorationMethod = "Seleccione un metodo valido.";
      }
      validateDay("closingDay", "El dia sugerido de cierre");
      validateDay("dueDay", "El dia de vencimiento");
      if (form.graceDays !== "") {
        const value = Number(form.graceDays);
        if (!Number.isInteger(value) || value < 0) {
          errors.graceDays = "Los dias de gracia deben ser mayor o igual a 0.";
        }
      }

      return !Object.values(errors).some(Boolean);
    };

    const numberOrNull = (value) =>
      value === "" || value === undefined || value === null ? null : Number(value);
    const numberOrZero = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const buildPayload = () => ({
      closingType: form.closingType,
      commonExpenseFixedAmount: numberOrZero(form.commonExpenseFixedAmount),
      commonExpenseUnitAmount: numberOrZero(form.commonExpenseUnitAmount),
      reserveFundType: form.reserveFundType,
      reserveFundValue: numberOrZero(form.reserveFundValue),
      reserveFundPercentage: numberOrZero(form.reserveFundPercentage),
      reserveFundUnitAmount: numberOrZero(form.reserveFundUnitAmount),
      prorationMethod: form.prorationMethod,
      mainCurrencyId: numberOrNull(form.mainCurrencyId),
      closingDay: numberOrNull(form.closingDay),
      dueDay: numberOrNull(form.dueDay),
      graceDays: numberOrZero(form.graceDays),
    });

    const loadConfiguration = async () => {
      if (!buildingId.value) return;
      store.commit("setLoading", true);
      try {
        if (!store.state.general.currencies?.length) {
          await store.dispatch("GENERAL_DATA");
        }
        const response = await AccountingConfigurationService.getConfiguration(buildingId.value);
        if (response?.ok) {
          assignForm(response.configuration || response.data);
        } else if (response?.status === 404) {
          assignForm();
        } else {
          state.generalError =
            response?.message || "No fue posible cargar la configuracion.";
        }
      } finally {
        store.commit("setLoading", false);
      }
    };

    const onSave = async () => {
      if (!buildingId.value) {
        state.generalError = "Debe seleccionar un edificio.";
        return;
      }
      if (!validate()) return;
      store.commit("setLoading", true);
      try {
        const response = await AccountingConfigurationService.saveConfiguration(
          buildingId.value,
          buildPayload()
        );
        if (response?.ok) {
          assignForm(response.configuration || response.data);
          store.commit("setToast", {
            type: "success",
            message: "Configuracion de cierres mensuales guardada correctamente.",
          });
        } else {
          state.generalError =
            response?.message || "No fue posible guardar la configuracion.";
        }
      } finally {
        store.commit("setLoading", false);
      }
    };

    const onCancel = () => {
      router.push("/contabilidad");
    };

    onMounted(loadConfiguration);

    watch(buildingId, async (value, oldValue) => {
      if (value && value !== oldValue) {
        await loadConfiguration();
      }
    });

    return {
      form,
      errors,
      generalError,
      buildingId,
      closingTypeOptions,
      reserveFundTypeOptions,
      prorationMethodOptions,
      currencyOptions,
      onSave,
      onCancel,
    };
  },
};
</script>

<style scoped>
.container {
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.configuration-form {
  align-items: center;
  display: flex;
  flex-direction: column;
  margin: 2rem 0;
  max-width: 720px;
  width: 100%;
}

.form-section {
  border-bottom: 1px solid rgba(35, 141, 130, 0.22);
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  width: 100%;
}

.form-section h2 {
  color: var(--primary);
  font-family: "klavika";
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1rem;
}

.form-section > * {
  max-width: 350px;
}

.helper,
.notice,
.rules {
  color: #4b5563;
  font-family: "klavika";
  font-size: 0.95rem;
  line-height: 1.5;
}

.notice {
  margin-top: 2rem;
  text-align: center;
}

.rules {
  margin: 0;
  padding-left: 1.25rem;
}

.actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 0.5rem;
}

.secondary-button {
  background: #ffffff;
  border: 1px solid var(--primary);
  border-radius: 0.5rem;
  color: var(--primary);
  cursor: pointer;
  font-family: "klavika";
  font-size: 1.1rem;
  height: 50px;
  width: 190px;
}

@media (max-width: 640px) {
  .configuration-form {
    padding: 0 1rem;
  }

  .actions {
    align-items: center;
    flex-direction: column;
  }
}
</style>
